import { useAccount, useReadContract, useReadContracts, usePublicClient } from 'wagmi'
import { Address, formatUnits } from 'viem'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { useELendsList } from '@/domain/euler/elends/useELendInfo'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { getSubAccountAddress, FALLBACK_SUBACCOUNT_RANGE } from '@/domain/euler/shared/subAccounts'
import { useSubaccountStore } from '@/domain/euler/subaccounts'

const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60

export interface EBorrowPosition {
  collateralToken: Token
  liabilityToken: Token
  collateralVaultAddress: Address
  liabilityVaultAddress: Address
  collateralAmount: NormalizedUnitNumber
  borrowAmount: NormalizedUnitNumber
  borrowAPY: Percentage
  chainId: number
  rowClickOptions: { destination: string }
  /** Sub-account ID (0 = main account, 1-255 = sub-accounts) */
  subAccountId: number
  /** The sub-account address for this position */
  subAccountAddress: Address
}

export interface UseEBorrowPositionsParams {
  userAddress?: Address
  chainId?: number
  enabled?: boolean
}

export interface UseEBorrowPositionsResult {
  positions: EBorrowPosition[]
  isLoading: boolean
  error: Error | null
}

/**
 * Hook to fetch eBorrow positions from onchain across all sub-accounts
 * Fetches user's borrow positions via EVC controllers and collaterals
 * Uses the global subaccount store to query only known active subaccounts
 * Reuses eLend vault metadata to avoid redundant fetching
 */
export function useEBorrowPositions({
  userAddress,
  chainId = E_LEND_CHAIN_ID,
  enabled = true,
}: UseEBorrowPositionsParams = {}): UseEBorrowPositionsResult {
  const { address: connectedAddress } = useAccount()
  const address = userAddress || connectedAddress

  // Get active subaccount IDs from the global discovery cache
  const { activeSubaccountIds, isDiscovering } = useSubaccountStore()

  // Step 1: Get all eLend vaults with their metadata (reuse existing data)
  const { vaults: elendVaults, isLoading: isLoadingVaults } = useELendsList(chainId)
  const publicClient = usePublicClient({ chainId })

  // Step 2: Fetch EVC address from the first available vault
  const firstVaultAddress = elendVaults[0]?.address
  const { data: evcAddressData, isLoading: isLoadingEvc } = useReadContract({
    address: firstVaultAddress,
    abi: eVaultImplementationAbi,
    functionName: 'EVC',
    chainId,
    query: {
      enabled: !!firstVaultAddress,
      staleTime: 60_000, // Cache for 1 minute - EVC address doesn't change
    },
  })

  const evcAddress = evcAddressData as Address | undefined

  // Generate sub-account addresses to query
  // Include fallback range (0-15) for safety, plus any higher active subaccounts from discovery
  const subAccountAddresses = useMemo(() => {
    if (!address) return []
    // Create a unique set of IDs: fallback range + discovered active IDs
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    const idsToQuery = [...new Set([...fallbackIds, ...activeSubaccountIds])]
    return idsToQuery.map((id) => ({
      subAccountId: id,
      subAccountAddress: getSubAccountAddress(address, id),
    }))
  }, [address, activeSubaccountIds])

  // Step 3: Query controllers and collaterals for ALL sub-accounts in a single multicall
  const subAccountQueryContracts = useMemo(() => {
    if (!evcAddress || !address || !enabled) return []
    return subAccountAddresses.flatMap(({ subAccountAddress }) => [
      {
        address: evcAddress,
        abi: eulerVaultConnectorAbi,
        functionName: 'getControllers' as const,
        args: [subAccountAddress] as const,
        chainId,
      },
      {
        address: evcAddress,
        abi: eulerVaultConnectorAbi,
        functionName: 'getCollaterals' as const,
        args: [subAccountAddress] as const,
        chainId,
      },
    ])
  }, [evcAddress, address, enabled, subAccountAddresses, chainId])

  const { data: subAccountData, isLoading: isLoadingSubAccounts } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: subAccountQueryContracts as any,
    query: {
      enabled: enabled && !!address && !!evcAddress && subAccountQueryContracts.length > 0,
      staleTime: 30_000,
    },
  })

  // Parse sub-account data into a map of subAccountId -> { controllers, collaterals }
  const subAccountPositionsMap = useMemo(() => {
    const map = new Map<number, { controllers: Address[]; collaterals: Address[]; subAccountAddress: Address }>()
    if (!subAccountData || !address) return map

    // Iterate over the subaccount IDs we actually queried
    for (let i = 0; i < subAccountAddresses.length; i++) {
      const { subAccountId, subAccountAddress } = subAccountAddresses[i]!
      const dataIndex = i * 2
      const controllers = (subAccountData[dataIndex]?.result as Address[]) || []
      const collaterals = (subAccountData[dataIndex + 1]?.result as Address[]) || []

      // Only add if there are controllers (positions with debt)
      if (controllers.length > 0) {
        map.set(subAccountId, {
          controllers,
          collaterals,
          subAccountAddress,
        })
      }
    }
    return map
  }, [subAccountData, address, subAccountAddresses])

  // Get all unique vault addresses across all sub-accounts
  const allPositionVaultAddresses = useMemo(() => {
    const vaults = new Set<Address>()
    for (const { controllers, collaterals } of subAccountPositionsMap.values()) {
      for (const v of controllers) vaults.add(v)
      for (const v of collaterals) vaults.add(v)
    }
    return [...vaults]
  }, [subAccountPositionsMap])

  // Get all sub-accounts that have positions (for querying balances)
  const activeSubAccounts = useMemo(() => {
    return [...subAccountPositionsMap.entries()].map(([id, data]) => ({
      subAccountId: id,
      ...data,
    }))
  }, [subAccountPositionsMap])

  // Step 3.5: Identify vaults not in indexer and fetch their metadata on-chain (fallback)
  const vaultsNotInIndexer = useMemo(() => {
    const indexerVaultAddresses = new Set(elendVaults.map((v) => v.address.toLowerCase()))
    return allPositionVaultAddresses.filter((addr) => !indexerVaultAddresses.has(addr.toLowerCase()))
  }, [allPositionVaultAddresses, elendVaults])

  // Fetch full vault metadata for vaults not in indexer using existing onchain fetcher
  const { data: missingVaultsMetadata } = useQuery({
    queryKey: ['eborrow-missing-vaults', chainId, vaultsNotInIndexer.join(',')],
    queryFn: async () => {
      if (!publicClient || vaultsNotInIndexer.length === 0) return []

      const { fetchELendOnchain } = await import('@/domain/euler/elends/onchainELendFetcher')

      // Fetch all missing vaults in parallel
      const promises = vaultsNotInIndexer.map((vaultAddress) =>
        fetchELendOnchain(publicClient, chainId, vaultAddress).catch((err) => {
           
          console.error(`Failed to fetch vault ${vaultAddress}:`, err)
          return null
        }),
      )

      const results = await Promise.all(promises)
      return results.filter((v): v is NonNullable<typeof v> => v !== null)
    },
    enabled: vaultsNotInIndexer.length > 0 && !!publicClient,
    staleTime: 60_000, // 1 minute
  })

  // Step 4: Fetch user-specific data for ALL sub-accounts and their vaults
  // Structure: for each active sub-account, query balanceOf/debtOf for each of their vaults
  const userDataContracts = useMemo(() => {
    if (!address || !enabled || activeSubAccounts.length === 0) return []

    const contracts: Array<{
      address: Address
      abi: typeof eVaultImplementationAbi
      functionName: 'balanceOf' | 'debtOf' | 'convertToAssets' | 'interestRate'
      args?: readonly [Address] | readonly [bigint]
      chainId: number
    }> = []

    // First, add vault metadata queries (convertToAssets, interestRate) for all unique vaults
    for (const vaultAddress of allPositionVaultAddresses) {
      contracts.push({
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'convertToAssets' as const,
        args: [BigInt(10 ** 18)] as const,
        chainId,
      })
      contracts.push({
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'interestRate' as const,
        chainId,
      })
    }

    // Then, add per-sub-account queries for balanceOf/debtOf
    for (const { subAccountAddress, controllers, collaterals } of activeSubAccounts) {
      const vaults = [...new Set([...controllers, ...collaterals])]
      for (const vaultAddress of vaults) {
        contracts.push({
          address: vaultAddress,
          abi: eVaultImplementationAbi,
          functionName: 'balanceOf' as const,
          args: [subAccountAddress] as const,
          chainId,
        })
        contracts.push({
          address: vaultAddress,
          abi: eVaultImplementationAbi,
          functionName: 'debtOf' as const,
          args: [subAccountAddress] as const,
          chainId,
        })
      }
    }

    return contracts
  }, [address, enabled, activeSubAccounts, allPositionVaultAddresses, chainId])

  const { data: userData, isLoading: isLoadingUserData } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: userDataContracts as any,
    query: {
      enabled: enabled && !!address && userDataContracts.length > 0,
      staleTime: 30_000,
    },
  })

  // Step 5: Build positions by combining vault metadata with user data for each sub-account
  const positions: EBorrowPosition[] = useMemo(() => {
    if (!address || !userData || activeSubAccounts.length === 0) return []

    // Create vault metadata map
    const allVaults = [...elendVaults, ...(missingVaultsMetadata || [])]
    const vaultMetadataMap = new Map(allVaults.map((v) => [v.address.toLowerCase(), v]))

    // Parse vault-level data (convertToAssets, interestRate)
    const vaultDataMap = new Map<string, { sharePrice: bigint; interestRate: bigint }>()
    let dataIndex = 0
    for (const vaultAddress of allPositionVaultAddresses) {
      vaultDataMap.set(vaultAddress.toLowerCase(), {
        sharePrice: (userData[dataIndex]?.result as bigint) || BigInt(10 ** 18),
        interestRate: (userData[dataIndex + 1]?.result as bigint) || 0n,
      })
      dataIndex += 2
    }

    // Parse per-sub-account data (balanceOf, debtOf)
    type SubAccountVaultData = Map<string, { balance: bigint; debt: bigint }>
    const subAccountDataMap = new Map<number, SubAccountVaultData>()

    for (const { subAccountId, controllers, collaterals } of activeSubAccounts) {
      const vaults = [...new Set([...controllers, ...collaterals])]
      const vaultData: SubAccountVaultData = new Map()

      for (const vaultAddress of vaults) {
        vaultData.set(vaultAddress.toLowerCase(), {
          balance: (userData[dataIndex]?.result as bigint) || 0n,
          debt: (userData[dataIndex + 1]?.result as bigint) || 0n,
        })
        dataIndex += 2
      }

      subAccountDataMap.set(subAccountId, vaultData)
    }

    const result: EBorrowPosition[] = []

    // Build positions for each sub-account
    for (const { subAccountId, subAccountAddress, controllers, collaterals } of activeSubAccounts) {
      const subAccountVaultData = subAccountDataMap.get(subAccountId)
      if (!subAccountVaultData) continue

      // Iterate through controllers (liability vaults) for this sub-account
      for (const liabilityVault of controllers) {
        const liabilityKey = liabilityVault.toLowerCase()
        const liabilityMetadata = vaultMetadataMap.get(liabilityKey)
        const liabilityVaultData = vaultDataMap.get(liabilityKey)
        const liabilityUserData = subAccountVaultData.get(liabilityKey)

        // Skip if metadata missing, no user data, no debt, or no asset address
        if (
          !liabilityMetadata ||
          !liabilityMetadata.asset ||
          !liabilityVaultData ||
          !liabilityUserData ||
          liabilityUserData.debt === 0n
        )
          continue

        // Create liability token from vault metadata
        const liabilityToken = new Token({
          symbol: TokenSymbol(liabilityMetadata.assetSymbol || 'UNKNOWN'),
          name: liabilityMetadata.assetSymbol || 'UNKNOWN',
          decimals: liabilityMetadata.assetDecimals || 18,
          address: CheckedAddress(liabilityMetadata.asset),
          unitPriceUsd: liabilityMetadata.assetPrice?.toString() || '0',
          logoURI: undefined,
        })

        // Calculate borrow APY from interest rate
        const ratePerSecond = liabilityVaultData.interestRate
          ? Number(formatUnits(liabilityVaultData.interestRate, 27))
          : 0
        const borrowAPY = liabilityVaultData.interestRate ? ((1 + ratePerSecond) ** SECONDS_PER_YEAR - 1) * 100 : 0

        const borrowAmount = Number(formatUnits(liabilityUserData.debt, liabilityToken.decimals))

        // Find collateral for this liability (within the same sub-account)
        let collateralVault: Address | undefined
        let collateralToken = liabilityToken
        let collateralAmount = 0

        for (const collateral of collaterals) {
          const collateralKey = collateral.toLowerCase()
          const collateralMetadata = vaultMetadataMap.get(collateralKey)
          const collateralVaultData = vaultDataMap.get(collateralKey)
          const collateralUserData = subAccountVaultData.get(collateralKey)

          if (
            collateralMetadata?.asset &&
            collateralVaultData &&
            collateralUserData &&
            collateralUserData.balance > 0n
          ) {
            collateralVault = collateral
            collateralToken = new Token({
              symbol: TokenSymbol(collateralMetadata.assetSymbol || 'UNKNOWN'),
              name: collateralMetadata.assetSymbol || 'UNKNOWN',
              decimals: collateralMetadata.assetDecimals || 18,
              address: CheckedAddress(collateralMetadata.asset),
              unitPriceUsd: collateralMetadata.assetPrice?.toString() || '0',
              logoURI: undefined,
            })
            const collateralAssets = (collateralUserData.balance * collateralVaultData.sharePrice) / BigInt(10 ** 18)
            collateralAmount = Number(formatUnits(collateralAssets, collateralToken.decimals))
            break
          }
        }

        result.push({
          collateralToken,
          liabilityToken,
          collateralVaultAddress: collateralVault || liabilityVault,
          liabilityVaultAddress: liabilityVault,
          collateralAmount: NormalizedUnitNumber(collateralAmount),
          borrowAmount: NormalizedUnitNumber(borrowAmount),
          borrowAPY: Percentage(borrowAPY / 100, true, true),
          chainId,
          subAccountId,
          subAccountAddress,
          rowClickOptions: {
            destination: `/markets/eborrow/${collateralVault || liabilityVault}/${liabilityVault}?subAccountId=${subAccountId}`,
          },
        })
      }
    }

    return result
  }, [address, userData, activeSubAccounts, elendVaults, missingVaultsMetadata, allPositionVaultAddresses, chainId])

  // Loading state (include discovery status)
  const isLoading = isLoadingVaults || isLoadingEvc || isLoadingSubAccounts || isLoadingUserData || isDiscovering

  return {
    positions,
    isLoading,
    error: null,
  }
}
